// src/pages/PopCultureImageGenPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Wand2, Download, Loader2, Sparkles, Lightbulb, X, History, Image as ImageIcon, Settings, Trash2, Plus, Palette } from 'lucide-react';
import GeminiApiKeyInput from '../components/GeminiApiKeyInput';

const PopCultureImageGenPage = () => {
    const [prompt, setPrompt] = useState('');
    const [model, setModel] = useState('gemini-flash');
    const [aspectRatio, setAspectRatio] = useState('16:9');
    const [themeStyle, setThemeStyle] = useState('fantasy');
    const [customStyle, setCustomStyle] = useState('');
    const [popCulture, setPopCulture] = useState('');
    const [customPopCulture, setCustomPopCulture] = useState('');
    const [negativePrompt, setNegativePrompt] = useState('');
    const [referenceImage, setReferenceImage] = useState(null); // Base64 string

    const [loading, setLoading] = useState(false);
    const [enhancing, setEnhancing] = useState(false);
    const [generatingIdea, setGeneratingIdea] = useState(false);

    const [resultImage, setResultImage] = useState(null);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState('');
    const [geminiApiKey, setGeminiApiKey] = useState('');

    const [showImageModal, setShowImageModal] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    // Design Studio State
    const [activeTab, setActiveTab] = useState('popculture'); // 'popculture' or 'design'
    const [designStyle, setDesignStyle] = useState('vector');
    const [designLoading, setDesignLoading] = useState(false);

    const fileInputRef = useRef(null);

    // Design Styles for Print on Demand (Expanded)
    const designStyles = {
        'vector': { label: 'Vector Art', prompt: 'Flat vector art style, clean lines, solid colors, no gradients, svg style, high resolution, isolated on white background.' },
        'watercolor': { label: 'Watercolor', prompt: 'Watercolor painting style, artistic, soft edges, paint splashes, paper texture, isolated on white background.' },
        'pattern': { label: 'Seamless Pattern', prompt: 'Seamless repeating pattern design, tileable, flat 2D design, suitable for fabric or wallpaper.' },
        'line-art': { label: 'Line Art / Sketch', prompt: 'Black and white line art, minimalist sketch, ink drawing style, clear outlines, no shading, isolated on white background.' },
        'sticker': { label: 'Sticker Design', prompt: 'Die-cut sticker design, thick white border, bold colors, vector style, cute and expressive, isolated on dark background.' },
        'vintage': { label: 'Vintage / Retro', prompt: 'Vintage retro style, distressed texture, muted color palette, 70s/80s aesthetic, poster design.' },
        'oil-painting': { label: 'Oil Painting', prompt: 'Oil painting style, visible brush strokes, rich colors, artistic texture, canvas look.' },
        '3d-render': { label: '3D Render (Abstract)', prompt: 'Abstract 3D render, glossy materials, soft lighting, modern art style, isolated object.' },
        'ukiyo-e': { label: 'Ukiyo-e (Japanese)', prompt: 'Ukiyo-e Japanese woodblock print style, flat perspective, bold outlines, limited color palette, traditional texture.' },
        'synthwave': { label: 'Synthwave / Neon', prompt: 'Synthwave aesthetic, neon grid, sunset, retro-futuristic 80s vibes, glowing colors, dark background.' },
        'graffiti': { label: 'Graffiti / Street Art', prompt: 'Urban graffiti style, spray paint texture, drips, bold lettering, vibrant colors, street art aesthetic.' },
        'pixel-art': { label: 'Pixel Art', prompt: 'High quality pixel art, 8-bit or 16-bit style, dithering, retro game aesthetic, sharp edges.' },
        'low-poly': { label: 'Low Poly', prompt: 'Low poly 3D art, geometric shapes, faceted surfaces, flat shading, minimalist aesthetic.' },
        'paper-cutout': { label: 'Paper Cutout', prompt: 'Paper cutout art style, layered paper texture, depth shadows, craft aesthetic, collage look.' },
        'charcoal': { label: 'Charcoal Sketch', prompt: 'Charcoal drawing style, rough texture, smudges, dramatic lighting, black and white, artistic sketch.' }
    };

    const styleMap = {
        fantasy: "Fantasy illustration, dark academia, epic realism, reminiscent of classic D&D module art.",
        classicmap: "D&D Map (Classic): Hand-drawn cartography, parchment texture, compass rose, fantasy terrain symbols.",
        modernmap: "D&D Map (Modern): Digital battle map, grid overlay, detailed terrain, top-down tactical view.",
        photo: "A photorealistic image, high resolution, sharp focus, 8K, detailed, professional photography.",
        anime: "Anime style, vibrant colors, clean lines, cel-shaded, high-quality digital art.",
        cyberpunk: "High-tech, neon-dystopian visuals with digital noise and glitches.",
        pixelart: "Low-resolution graphics inspired by 8–16 bit games.",
        y2k: "Early-2000s internet + pop culture comeback with shiny, playful visuals.",
        vaporwave: "Dreamy, surreal remix of 80s/90s computer graphics and mall culture.",
        popfuturism: "Pop Futurism / Retro-Futurism: Optimistic space-age aesthetics, chrome, atomic patterns.",
        indiesleaze: "Indie Sleaze / Grunge 2.0: Flash photography, messy party vibes, vintage filters.",
        minimaluxe: "Clean Girl / Minimal Luxe: Soft neutrals, clean lines, effortless elegance.",
        cottagecore: "Cottagecore / Dark Cottagecore: Pastoral romance, florals, cozy or gothic countryside.",
        kawaii: "Kawaii / Pastel Anime-Core: Cute overload, pastel colors, chibi characters, sparkles.",
        surreal: "Surreal / Weird-Core: Dreamlike, uncanny, distorted reality, liminal spaces.",
        craftcore: "3D Craftcore / Soft 3D Blobs: Rounded 3D shapes, clay-like textures, playful design.",
        maxtypography: "Bold Maximalist Typography: Large text, vibrant colors, graphic design focus.",
        lofi: "Lofi / Cozy Core: Warm lighting, study vibes, relaxed atmosphere, nostalgic feel.",
        academia: "Dark Academia / Light Academia: Scholarly aesthetic, libraries, vintage books, moody or bright.",
        motoboho: "Moto Boho / Soft Grunge Decor: Eclectic mix, leather, plants, vintage posters.",
        custom: ""
    };

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showImageModal) {
                setShowImageModal(false);
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [showImageModal]);

    // Auto-open settings if no key is found initially (after a short delay to allow local storage load)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!geminiApiKey) setShowSettings(true);
        }, 1000);
        return () => clearTimeout(timer);
    }, [geminiApiKey]);

    const handleReferenceImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setReferenceImage(event.target.result);
        };
        reader.readAsDataURL(file);
    };

    const clearReferenceImage = () => {
        setReferenceImage(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleUploadKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
        }
    };

    const enhancePrompt = async () => {
        if (!prompt.trim()) {
            setError("Please enter a prompt first");
            return;
        }

        setEnhancing(true);
        setError('');

        try {
            if (!geminiApiKey) {
                setError("Please enter your Gemini API key first");
                setShowSettings(true);
                setEnhancing(false);
                return;
            }
            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${geminiApiKey}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contents: [{
                            parts: [{
                                text: `You are a creative prompt engineer. Enhance this image generation prompt to be more detailed and vivid, but YOU MUST KEEP the original subject, action, and core idea exactly as is. Do not change the scene, only add descriptive details to improve image quality (lighting, texture, mood). concise (2-3 sentences max). Original prompt: "${prompt}"`
                            }]
                        }]
                    })
                }
            );

            if (!response.ok) throw new Error('Failed to enhance prompt');
            const data = await response.json();
            const enhanced = data.candidates[0].content.parts[0].text.trim();
            setPrompt(enhanced);
        } catch (err) {
            console.error(err);
            setError("Failed to enhance prompt");
        } finally {
            setEnhancing(false);
        }
    };

    const generateIdeaFromPopCulture = async () => {
        if (!popCulture) {
            setError("Please select a pop culture option first");
            return;
        }

        if (popCulture === 'custom' && !customPopCulture.trim()) {
            setError("Please enter a custom pop culture topic");
            return;
        }

        setGeneratingIdea(true);
        setError('');

        const topic = popCulture === 'custom' ? customPopCulture : popCulture;

        try {
            if (!geminiApiKey) {
                setError("Please enter your Gemini API key first");
                setShowSettings(true);
                setGeneratingIdea(false);
                return;
            }
            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${geminiApiKey}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contents: [{
                            parts: [{
                                text: `Generate a completely RANDOM, unique, and creative image prompt inspired by: ${topic}. Do not use generic scenes. Think outside the box. Focus on a specific, unique scene, character, or moment that is rarely seen. Make it vivid and detailed (2-3 sentences). Avoid common tropes.`
                            }]
                        }],
                        generationConfig: {
                            temperature: 0.9,
                            topK: 40,
                            topP: 0.95,
                        }
                    })
                }
            );

            if (!response.ok) throw new Error('Failed to generate idea');
            const data = await response.json();
            const idea = data.candidates[0].content.parts[0].text.trim();
            setPrompt(idea);
        } catch (err) {
            console.error(err);
            setError("Failed to generate idea");
        } finally {
            setGeneratingIdea(false);
        }
    };

    const generateImage = async () => {
        if (!prompt.trim() && !referenceImage) {
            setError("Please enter a prompt or upload a reference image");
            return;
        }

        setLoading(true);
        setError('');
        setResultImage(null);

        try {
            if (!geminiApiKey) {
                setError("Please enter your Gemini API key first");
                setShowSettings(true);
                setLoading(false);
                return;
            }

            let finalPrompt = prompt;
            if (popCulture) {
                const cultureContext = popCulture === 'custom' ? customPopCulture : popCulture;
                if (cultureContext) {
                    finalPrompt += `. Franchise/Context: ${cultureContext}`;
                }
            }

            const styleDescription = themeStyle === 'custom' ? customStyle : styleMap[themeStyle];
            if (styleDescription) {
                finalPrompt += `. Style: ${styleDescription}`;
            }

            await executeGeneration(finalPrompt);

        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to generate image");
        } finally {
            setLoading(false);
        }
    };

    const generateDesign = async () => {
        if (!prompt.trim()) {
            setError("Please describe the design you want to create");
            return;
        }

        setDesignLoading(true);
        setError('');
        setResultImage(null);

        try {
            if (!geminiApiKey) {
                setError("Please enter your Gemini API key first");
                setShowSettings(true);
                setDesignLoading(false);
                return;
            }

            const stylePrompt = designStyles[designStyle]?.prompt || '';

            // Construct a prompt specifically for POD/Art
            let fullDesignPrompt = `Create a high-quality ${designStyles[designStyle]?.label || 'art'} design. `;
            fullDesignPrompt += `Subject: ${prompt}. `;

            // Add Pop Culture Context if selected
            if (popCulture) {
                const cultureContext = popCulture === 'custom' ? customPopCulture : popCulture;
                if (cultureContext) {
                    fullDesignPrompt += `Theme/Franchise: ${cultureContext}. `;
                }
            }

            fullDesignPrompt += `Style: ${stylePrompt} `;
            fullDesignPrompt += `Requirements: High resolution, suitable for printing on products (T-shirts, mugs, canvases). `;
            fullDesignPrompt += `NEGATIVE PROMPT: Do not generate a realistic scene, room, furniture, wall, frame, or mockup. Do not show the artwork displayed on a product. Generate ONLY the raw artwork/design itself. No text unless specified.`;

            await executeGeneration(fullDesignPrompt, true);

        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to generate design");
        } finally {
            setDesignLoading(false);
        }
    };

    const executeGeneration = async (finalPrompt, isDesignMode = false) => {
        let apiUrl, payload, base64Data;

        const imagenModels = {
            'imagen-fast': 'imagen-4.0-fast-generate-001',
            'imagen': 'imagen-4.0-generate-001',
            'imagen-ultra': 'imagen-4.0-ultra-generate-001'
        };
        const geminiModels = {
            'gemini-flash': 'gemini-2.5-flash-image',
            'nano-banana-2': 'gemini-3.1-flash-image-preview',
            'gemini-pro': 'gemini-3-pro-image-preview'
        };

        if (imagenModels[model]) {
            if (referenceImage) {
                throw new Error("Imagen models do not currently support reference images. Please use a Gemini model.");
            }

            apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${imagenModels[model]}:predict?key=${geminiApiKey}`;
            payload = {
                instances: [{ prompt: finalPrompt }],
                parameters: {
                    sampleCount: 1,
                    aspectRatio: aspectRatio
                }
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errBody = await response.json().catch(() => ({}));
                const detail = errBody?.error?.message || response.statusText;
                throw new Error(`Imagen API Error (${response.status}): ${detail}`);
            }
            const result = await response.json();

            if (result.predictions && result.predictions[0]?.bytesBase64Encoded) {
                base64Data = result.predictions[0].bytesBase64Encoded;
            } else {
                throw new Error("Imagen returned no image data. Check that your API key has Imagen access enabled.");
            }
        } else {
            // Gemini Image Generation
            const modelName = geminiModels[model] || 'gemini-2.5-flash-image';
            apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${geminiApiKey}`;

            if (negativePrompt) {
                finalPrompt += `\nNegative Prompt: ${negativePrompt}`;
            }

            const parts = [{ text: finalPrompt }];

            if (referenceImage) {
                const base64Image = referenceImage.split(',')[1];
                const mimeMatch = referenceImage.match(/data:(image\/\w+);base64/);
                const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
                parts.push({
                    inlineData: { mimeType, data: base64Image }
                });
            }

            payload = {
                contents: [{ parts }],
                generationConfig: {
                    responseModalities: ['TEXT', 'IMAGE'],
                    imageConfig: { aspectRatio: aspectRatio }
                }
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errBody = await response.json().catch(() => ({}));
                const detail = errBody?.error?.message || response.statusText;
                throw new Error(`Gemini API Error (${response.status}): ${detail}`);
            }
            const result = await response.json();

            const part = result?.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
            if (part?.inlineData?.data) {
                base64Data = part.inlineData.data;
            } else {
                // Surface any text response for debugging
                const textPart = result?.candidates?.[0]?.content?.parts?.find(p => p.text);
                const reason = result?.candidates?.[0]?.finishReason;
                throw new Error(
                    textPart?.text
                        ? `Model returned text instead of image: "${textPart.text.slice(0, 120)}"`
                        : `No image returned. Finish reason: ${reason || 'unknown'}`
                );
            }
        }

        const newImage = `data:image/png;base64,${base64Data}`;
        setResultImage(newImage);

        // Add to history
        setHistory(prev => [{
            id: Date.now(),
            url: newImage,
            prompt: finalPrompt,
            type: isDesignMode ? 'design' : 'popculture',
            timestamp: new Date().toLocaleTimeString()
        }, ...prev]);
    };

    const handleDownload = (imageUrl) => {
        if (imageUrl) {
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = `ai-generated-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="flex-grow bg-gradient-to-br from-gray-900 via-slate-900 to-black min-h-screen text-slate-200 p-4 md:p-8 animate-fade-in flex flex-col">
            <div className="max-w-7xl mx-auto w-full space-y-6 flex-grow">

                {/* Header & Settings Toggle */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                            AI Image Generator
                        </h1>
                        <p className="text-slate-400 text-sm">Pop Culture & Design Studio</p>
                    </div>
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-2 rounded-full transition-colors ${showSettings ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                        title="API Key Settings"
                        aria-label="Toggle settings"
                    >
                        <Settings size={24} />
                    </button>
                </div>

                {/* Mode Tabs */}
                <div className="flex gap-4 border-b border-slate-700 pb-1">
                    <button
                        onClick={() => setActiveTab('popculture')}
                        className={`pb-2 px-4 font-semibold transition-colors flex items-center gap-2 ${activeTab === 'popculture' ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                        <Sparkles size={16} /> Pop Culture
                    </button>
                    <button
                        onClick={() => setActiveTab('design')}
                        className={`pb-2 px-4 font-semibold transition-colors flex items-center gap-2 ${activeTab === 'design' ? 'text-pink-400 border-b-2 border-pink-400' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                        <Palette size={16} /> Design Studio
                    </button>
                </div>

                {/* Collapsible Settings Panel */}
                {showSettings && (
                    <div className="animate-fade-in">
                        <GeminiApiKeyInput onApiKeyChange={setGeminiApiKey} />
                    </div>
                )}

                {/* Main Prompt Section (Full Width) */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 shadow-xl backdrop-blur-md animate-slide-up">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-semibold text-white flex items-center gap-2">
                            <Sparkles size={16} className="text-indigo-400" />
                            Creative Prompt
                        </label>
                        <button
                            onClick={enhancePrompt}
                            disabled={enhancing || !prompt.trim()}
                            className="text-xs flex items-center gap-1 text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                        >
                            {enhancing ? <Loader2 className="animate-spin" size={12} /> : <Wand2 size={12} />}
                            Enhance with AI
                        </button>
                    </div>
                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        className="w-full p-4 bg-slate-900/50 border border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none resize-none text-slate-200 text-lg leading-relaxed shadow-inner"
                        placeholder="Describe your imagination... (e.g., A cyberpunk samurai standing in neon rain)"
                        style={{ minHeight: '120px', maxHeight: '300px' }}
                    />
                </div>

                {/* Controls & Options Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* Left: Configuration */}
                    <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Model & Ratio */}
                        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Model</label>
                                <select
                                    value={model}
                                    onChange={(e) => setModel(e.target.value)}
                                    className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                >
                                    <option value="imagen-fast">Imagen 4 Fast — $0.02/img</option>
                                    <option value="gemini-flash">Nano Banana (Gemini 2.5 Flash) — $0.039/img</option>
                                    <option value="imagen">Imagen 4 — $0.04/img</option>
                                    <option value="imagen-ultra">Imagen 4 Ultra — $0.06/img</option>
                                    <option value="nano-banana-2">Nano Banana 2 (Gemini 3.1 Flash) — $0.067/img</option>
                                    <option value="gemini-pro">Nano Banana Pro (Gemini 3 Pro) — $0.134/img</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Aspect Ratio</label>
                                <select
                                    value={aspectRatio}
                                    onChange={(e) => setAspectRatio(e.target.value)}
                                    className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                >
                                    <option value="16:9">Landscape (16:9)</option>
                                    <option value="1:1">Square (1:1)</option>
                                    <option value="21:9">Wide (21:9)</option>
                                    <option value="9:16">Portrait (9:16)</option>
                                    <option value="3:4">Portrait (3:4)</option>
                                    <option value="4:3">Landscape (4:3)</option>
                                </select>
                            </div>
                        </div>

                        {/* Style & Pop Culture */}
                        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Style</label>
                                {activeTab === 'popculture' ? (
                                    <select
                                        value={themeStyle}
                                        onChange={(e) => setThemeStyle(e.target.value)}
                                        className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                    >
                                        <option value="fantasy">Fantasy Illustration (D&D Style)</option>
                                        <option value="classicmap">D&D Map (Classic)</option>
                                        <option value="modernmap">D&D Map (Modern)</option>
                                        <option value="photo">Photorealistic</option>
                                        <option value="anime">Anime / Manga</option>
                                        <option value="cyberpunk">Cyberpunk / Glitch</option>
                                        <option value="pixelart">Pixel Art / Retro Game</option>
                                        <option value="y2k">Y2K / Nostalgia-Core</option>
                                        <option value="vaporwave">Vaporwave / Retro Internet</option>
                                        <option value="popfuturism">Pop Futurism / Retro-Futurism</option>
                                        <option value="indiesleaze">Indie Sleaze / Grunge 2.0</option>
                                        <option value="minimaluxe">Clean Girl / Minimal Luxe</option>
                                        <option value="cottagecore">Cottagecore / Dark Cottagecore</option>
                                        <option value="kawaii">Kawaii / Pastel Anime-Core</option>
                                        <option value="surreal">Surreal / Weird-Core</option>
                                        <option value="craftcore">3D Craftcore / Soft 3D Blobs</option>
                                        <option value="maxtypography">Bold Maximalist Typography</option>
                                        <option value="lofi">Lofi / Cozy Core</option>
                                        <option value="academia">Dark Academia / Light Academia</option>
                                        <option value="motoboho">Moto Boho / Soft Grunge Decor</option>
                                        <option value="custom">Custom Style</option>
                                    </select>
                                ) : (
                                    <select
                                        value={designStyle}
                                        onChange={(e) => setDesignStyle(e.target.value)}
                                        className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-pink-500 outline-none"
                                    >
                                        {Object.entries(designStyles).map(([key, style]) => (
                                            <option key={key} value={key}>{style.label}</option>
                                        ))}
                                    </select>
                                )}
                                {activeTab === 'popculture' && themeStyle === 'custom' && (
                                    <input
                                        type="text"
                                        value={customStyle}
                                        onChange={(e) => setCustomStyle(e.target.value)}
                                        className="mt-2 w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                                        placeholder="Describe style..."
                                    />
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Pop Culture Theme</label>
                                <select
                                    value={popCulture}
                                    onChange={(e) => setPopCulture(e.target.value)}
                                    className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                >
                                    <option value="">-- None --</option>
                                    <option value="custom">Custom Topic</option>
                                    <optgroup label="TV/Streaming">
                                        <option value="Stranger Things: 80s sci-fi horror; retro tech, neon, monsters.">Stranger Things</option>
                                        <option value="Wednesday: Goth teen, spooky school; black-and-white + purple palette.">Wednesday</option>
                                        <option value="Squid Game: Korean survival games; guards' masks, tracksuits, shapes.">Squid Game</option>
                                        <option value="Percy Jackson: Modern Greek mythology; camp logos, gods, symbols.">Percy Jackson</option>
                                        <option value="Doctor Who: Time-travel sci-fi; TARDIS police box, galaxies.">Doctor Who</option>
                                        <option value="One Piece (Live Action): Pirate adventure; straw hat, Jolly Roger, ships.">One Piece (Live Action)</option>
                                    </optgroup>
                                    <optgroup label="Games">
                                        <option value="Fortnite: Battle royale; neon skins, cartoony weapons, emote culture.">Fortnite</option>
                                        <option value="Minecraft: Sandbox; block worlds, creepers, pixel tools.">Minecraft</option>
                                        <option value="Roblox: UGC game platform; blocky avatars, mini-game icons.">Roblox</option>
                                        <option value="Pokémon: Cute creatures; Pokéballs, Pikachu, type icons.">Pokémon</option>
                                        <option value="Genshin Impact: Anime fantasy; elemental symbols, lush landscapes.">Genshin Impact</option>
                                        <option value="Five Nights at Freddy's: Animatronic horror; creepy mascots, neon signage.">Five Nights at Freddy's</option>
                                    </optgroup>
                                    <optgroup label="Anime/Manga">
                                        <option value="Jujutsu Kaisen: Cursed spirits; urban night scenes, glowing effects, spell circles.">Jujutsu Kaisen</option>
                                        <option value="Demon Slayer: Taisho era demon hunters; elemental sword trails, patterned haori.">Demon Slayer</option>
                                        <option value="One Piece: Pirate epic; Straw Hat crew, maps, treasure, sea monsters.">One Piece</option>
                                        <option value="Attack on Titan: Wall cities, Titans, Scout wings emblem, grim landscapes.">Attack on Titan</option>
                                        <option value="My Hero Academia: Hero school; bold comic panels, costume logos, motion lines.">My Hero Academia</option>
                                        <option value="Chainsaw Man: Gory urban chaos; chainsaws, high-contrast red/orange.">Chainsaw Man</option>
                                    </optgroup>
                                </select>
                                {popCulture === 'custom' && (
                                    <input
                                        type="text"
                                        value={customPopCulture}
                                        onChange={(e) => setCustomPopCulture(e.target.value)}
                                        className="mt-2 w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                                        placeholder="Enter topic..."
                                    />
                                )}
                            </div>
                        </div>

                        {/* Negative Prompt (Only for Gemini) */}
                        {model !== 'imagen' && (
                            <div className="md:col-span-2 bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Negative Prompt (Optional)</label>
                                <input
                                    type="text"
                                    value={negativePrompt}
                                    onChange={(e) => setNegativePrompt(e.target.value)}
                                    className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                    placeholder="e.g., distorted hands, extra limbs, blurry..."
                                />
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="md:col-span-2 flex gap-3">
                            <button
                                onClick={generateIdeaFromPopCulture}
                                disabled={generatingIdea || !popCulture}
                                className="flex-1 bg-cyan-900/50 hover:bg-cyan-800/50 border border-cyan-700 text-cyan-200 py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
                            >
                                {generatingIdea ? <Loader2 className="animate-spin" size={18} /> : <Lightbulb size={18} />}
                                Generate Idea
                            </button>
                            <button
                                onClick={activeTab === 'popculture' ? generateImage : generateDesign}
                                disabled={loading || designLoading}
                                className={`flex-[2] bg-gradient-to-r ${activeTab === 'popculture' ? 'from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500' : 'from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500'} text-white py-3 rounded-xl shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-bold text-lg`}
                            >
                                {(loading || designLoading) ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
                                {activeTab === 'popculture' ? 'Generate Image' : 'Generate Design'}
                            </button>
                        </div>

                        {error && (
                            <div className="md:col-span-2 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg text-center">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Right: Reference Image (Remix) */}
                    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 flex flex-col">
                        <label className="text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-2">
                            <ImageIcon size={14} />
                            Reference Image (Remix)
                        </label>
                        <div
                            className={`flex-grow border-2 border-dashed rounded-xl flex flex-col items-center justify-center p-4 transition-colors relative overflow-hidden ${referenceImage ? 'border-indigo-500/50 bg-slate-900/50' : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800'}`}
                        >
                            {referenceImage ? (
                                <>
                                    <img src={referenceImage} alt="Reference" className="max-w-full max-h-[150px] object-contain rounded-lg opacity-70" />
                                    <button
                                        onClick={clearReferenceImage}
                                        className="absolute top-2 right-2 p-1 bg-red-500/80 text-white rounded-full hover:bg-red-600"
                                    >
                                        <X size={14} />
                                    </button>
                                    <p className="text-xs text-indigo-300 mt-2 font-medium">Image Loaded</p>
                                </>
                            ) : (
                                <div
                                    className="text-center cursor-pointer w-full h-full flex flex-col items-center justify-center"
                                    onClick={() => fileInputRef.current?.click()}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={handleUploadKeyDown}
                                >
                                    <Plus size={32} className="text-slate-600 mb-2" />
                                    <p className="text-xs text-slate-500">Click to upload reference</p>
                                    <p className="text-[10px] text-slate-600 mt-1">(Gemini Models Only)</p>
                                </div>
                            )}
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleReferenceImageUpload}
                                accept="image/*"
                                className="hidden"
                            />
                        </div>
                    </div>
                </div>

                {/* Main Output Area */}
                <div className="bg-slate-950 rounded-2xl border border-slate-800 p-1 shadow-2xl min-h-[400px] flex items-center justify-center relative overflow-hidden group animate-slide-up">
                    {(loading || designLoading) ? (
                        <div className="text-center text-slate-500">
                            <Loader2 size={64} className="mx-auto mb-4 animate-spin text-indigo-400" />
                            <p className="text-lg font-medium">Generating your image…</p>
                        </div>
                    ) : resultImage ? (
                        <>
                            <img
                                src={resultImage}
                                alt={`Generated image: ${prompt.slice(0, 80)}${prompt.length > 80 ? '…' : ''} — style: ${activeTab === 'design' ? designStyles[designStyle]?.label : (themeStyle === 'custom' ? customStyle || 'custom' : themeStyle)}`}
                                className="max-w-full max-h-[600px] object-contain rounded-xl shadow-2xl cursor-pointer"
                                onClick={() => setShowImageModal(true)}
                            />
                            <div className="absolute bottom-4 right-4 flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleDownload(resultImage)}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold shadow-lg flex items-center gap-2"
                                >
                                    <Download size={18} />
                                    Download
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="text-center text-slate-600">
                            <Wand2 size={64} className="mx-auto mb-4 opacity-20" />
                            <p className="text-xl font-medium opacity-50">Ready to Create</p>
                        </div>
                    )}
                </div>

                {/* Session History Strip */}
                {history.length > 0 && (
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase flex items-center gap-2">
                            <History size={14} />
                            Session History
                        </label>
                        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                            {history.map((item) => (
                                <div key={item.id} className="flex-shrink-0 w-24 sm:w-32 group relative">
                                    <img
                                        src={item.url}
                                        alt="History"
                                        className="w-full h-24 sm:h-32 object-cover rounded-lg border border-slate-700 cursor-pointer hover:border-indigo-500 transition-colors"
                                        onClick={() => {
                                            setResultImage(item.url);
                                            setPrompt(item.prompt);
                                        }}
                                    />
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setHistory(prev => prev.filter(h => h.id !== item.id));
                                        }}
                                        aria-label="Remove from history"
                                        className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-red-500 transition-all"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>

            {/* Full Screen Modal */}
            {showImageModal && resultImage && (
                <div
                    className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setShowImageModal(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Enlarged image"
                >
                    <div className="relative max-w-[95vw] max-h-[95vh]">
                        <button
                            onClick={() => setShowImageModal(false)}
                            aria-label="Close enlarged image"
                            className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
                        >
                            <X size={32} />
                        </button>
                        <img
                            src={resultImage}
                            alt="Full Size"
                            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PopCultureImageGenPage;
